// Schedule › To schedule › Leads, Blueprint's (BL-189): requests not booked yet, open, booked or closed
// (a Segmented), each a RowList row with Close and Book it at rest; New lead is a FormSheet and closing one a
// FormDialog that asks why. What it does is use-leads.ts's, as Riverstone's Leads.tsx.
import { useState } from 'react';
import {
  FormDialog,
  Link,
  Notice,
  RowList,
  type Row,
  Segmented,
  SelectField,
  Skeleton,
  SkeletonGroup,
  TextField,
} from '@flowology/ui-web/fl';
import { FormSheet, Section } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { useSaveKey } from '../shared/save-key.ts';
import { phoneText } from '../types.ts';
import {
  closeReason,
  emptyLeadForm,
  LEAD_CLOSE_REASONS,
  LEAD_SOURCES,
  leadInput,
  sourceLabel,
  type Lead,
  type LeadForm,
  type LeadStatus,
} from './tobook-model.ts';
import { LEAD_TABS, noLeads, useLeads } from './use-leads.ts';

/** A lead's name, or how to reach them when it has none. */
export const leadName = (l: Pick<Lead, 'name' | 'phone' | 'email'>) =>
  l.name ?? (l.phone ? phoneText(l.phone) : (l.email ?? 'No name'));

/** Where it came from, when, and how to reach them. */
export function leadLine(
  l: Pick<Lead, 'source' | 'createdAt' | 'name' | 'phone' | 'email'>,
  timeZone: string,
) {
  const when = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(l.createdAt),
  );
  return `${sourceLabel(l.source)} · ${when}${l.name && l.phone ? ` · ${phoneText(l.phone)}` : ''}${
    l.email && (l.name || l.phone) ? ` · ${l.email}` : ''
  }`;
}

export function LeadsPart({
  timeZone,
  onBook,
  onChanged,
}: {
  timeZone: string;
  onBook: (lead: Lead) => void;
  /** A lead was added or closed: the Inbox count may change. */
  onChanged: () => void;
}) {
  const { status, setStatus, leads, total, next, moreBusy, error, said, more, added, closed } =
    useLeads(onChanged);
  const [adding, setAdding] = useState(false);
  const [closing, setClosing] = useState<Lead | null>(null);
  return (
    <Section title="Leads" {...(status === 'open' && leads ? { count: total } : {})}>
      <div className="bp-tobook-tools">
        <Segmented
          label="Which leads"
          items={LEAD_TABS.map((t) => ({ id: t.status, label: t.label }))}
          selectedKey={status}
          onSelectionChange={(id) => setStatus(id as LeadStatus)}
        />
        <Link onPress={() => setAdding(true)}>New lead</Link>
      </div>
      {said && <Notice tone="done" title={said} />}
      {error && (
        <p className="bp-tobook-bad" role="alert">
          {error}
        </p>
      )}
      {leads === null && !error ? (
        <SkeletonGroup label="Loading the leads">
          <Skeleton shape="row" width="full" />
          <Skeleton shape="row" width="full" />
        </SkeletonGroup>
      ) : leads && leads.length ? (
        <div>
          <RowList
            label="Leads"
            rows={leads.map((l): Row => ({
              id: l.id,
              name: leadName(l),
              ...(l.status === 'open'
                ? {}
                : { end: { word: l.status === 'booked' ? 'Booked' : 'Closed', tone: 'muted' as const } }),
              lines: [{ text: leadLine(l, timeZone) }, { text: l.summary, tone: 'ink' }],
              atRest: 2,
              actions:
                l.status === 'open'
                  ? [
                      { id: 'close', text: 'Close', onPress: () => setClosing(l) },
                      {
                        id: 'book',
                        text: 'Book it',
                        onPress: () => onBook(l),
                        label: `Book the lead from ${l.name ?? 'this caller'}`,
                      },
                    ]
                  : [],
            }))}
          />
          {next && (
            <p className="bp-tobook-more">
              <Link isDisabled={moreBusy} onPress={more}>
                Load more
              </Link>
            </p>
          )}
        </div>
      ) : (
        leads && <p className="bp-tobook-sub">{noLeads(status)}</p>
      )}
      <NewLeadSheet
        isOpen={adding}
        onClose={() => setAdding(false)}
        onAdded={(l) => {
          setAdding(false);
          added(l);
        }}
      />
      <CloseLeadDialog lead={closing} onClose={() => setClosing(null)} onClosed={(l) => closed(l)} />
    </Section>
  );
}

/** New lead: a request that isn't booked yet (leads.create); it waits here until it's booked or closed. */
function NewLeadSheet({
  isOpen,
  onClose,
  onAdded,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdded: (l: Lead) => void;
}) {
  const [f, setF] = useState<LeadForm>(emptyLeadForm);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setF(emptyLeadForm());
  }
  const save = useSaveKey(JSON.stringify(f));
  const set = (patch: Partial<LeadForm>) => setF((x) => ({ ...x, ...patch }));
  const built = leadInput(f);
  const [tried, setTried] = useState(false);
  return (
    <FormSheet
      isOpen={isOpen}
      onClose={() => {
        setTried(false);
        onClose();
      }}
      title="New lead"
      line="A request that isn’t booked yet. It waits here until it’s booked or closed."
      submitLabel="Save the lead"
      isDirty={JSON.stringify(f) !== JSON.stringify(emptyLeadForm())}
      validate={() => {
        setTried(true);
        return !('error' in built);
      }}
      note={tried && 'error' in built ? built.error : 'A phone or an email, so someone can call them back.'}
      onSubmit={async () => {
        if ('error' in built) return;
        try {
          onAdded(await client.call<Lead>('leads.create', built.input, { idempotencyKey: save.key }));
        } catch (e) {
          throw new Error(errorText(e), { cause: e });
        }
      }}
    >
      <SelectField
        label="Where it came from"
        value={f.source}
        onChange={(v) => set({ source: v as LeadForm['source'] })}
        options={LEAD_SOURCES.map((s) => ({ value: s.value, label: s.label }))}
      />
      <TextField
        label="What they want"
        multiline
        maxLength={4000}
        value={f.summary}
        onChange={(summary) => set({ summary })}
      />
      <TextField label="Name (optional)" maxLength={200} value={f.name} onChange={(name) => set({ name })} />
      <TextField label="Phone" type="tel" value={f.phone} onChange={(phone) => set({ phone })} />
      <TextField label="Email" type="email" value={f.email} onChange={(email) => set({ email })} />
    </FormSheet>
  );
}

/** Close a lead with no job: a reason from the list, or written (leads.close). */
function CloseLeadDialog({
  lead,
  onClose,
  onClosed,
}: {
  lead: Lead | null;
  onClose: () => void;
  onClosed: (l: Lead) => void;
}) {
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');
  const [opened, setOpened] = useState<string | null>(null);
  if ((lead?.id ?? null) !== opened) {
    setOpened(lead?.id ?? null);
    setChoice('');
    setNote('');
  }
  const reason = closeReason(choice, note);
  const save = useSaveKey(`close:${lead?.id}:${reason}`);
  return (
    <FormDialog
      isOpen={!!lead}
      onClose={onClose}
      title="Close this lead with no job?"
      submitLabel="Close the lead"
      cancelLabel="Keep it open"
      isDirty={!!(choice || note)}
      isSubmitDisabled={!reason}
      note={lead ? `${lead.name ?? 'This lead'}: ${lead.summary}` : null}
      onSubmit={async () => {
        if (!lead || !reason) return;
        try {
          await client.call('leads.close', { id: lead.id, reason }, { idempotencyKey: save.key });
        } catch (e) {
          throw new Error(errorText(e), { cause: e });
        }
        onClosed(lead);
      }}
    >
      <SelectField
        label="Reason (required)"
        value={choice}
        onChange={setChoice}
        options={[
          { value: '', label: 'Something else (write it below)' },
          ...LEAD_CLOSE_REASONS.map((r) => ({ value: r, label: r })),
        ]}
      />
      <TextField
        label={choice ? 'Note (optional)' : 'Why'}
        multiline
        maxLength={400}
        value={note}
        onChange={setNote}
      />
    </FormDialog>
  );
}
